'use client';

import './payment-settings.scss';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { IntlBlock } from '@sola/ark-core/atoms/intl/Intl.tsx';
import { Alert } from '@sola/ark-core/molecules/alert/alert.tsx';
import type { GraphQLCommitment } from '@sola/schema/dotcom/supporters/schema.generated.js';
import { usePaymentMethodsWithCommitments } from '@sola/supporter-sdk/hooks/payment-methods.ts';

import { LocalAlertsProvider } from '../../molecules/local-alerts/local-alerts-provider.tsx';
import { LocalAlerts } from '../../molecules/local-alerts/local-alerts.tsx';
import { MakeOneTimePaymentAlert } from '../../molecules/make-one-time-payment-alert/make-one-time-payment-alert.tsx';
import { PaymentMethodCard } from '../../molecules/payment-method-card/payment-method-card.tsx';
import { PrimerError } from '../../molecules/primer-error/primer-error.tsx';
import { SectionContent } from '../../molecules/section-content/section-content.tsx';
import { PaymentIssueAlert } from '../payment-issue-alert/payment-issue-alert.tsx';
import { PaymentMethodAddSheet } from '../payment-method-add-sheet/payment-method-add-sheet.tsx';
import {
  AddPaymentMethodButton,
  AddPaymentMethodButtonLabel,
} from './payment-settings-add.tsx';

const useComponentData = () => {
  const {
    isLoading,
    hasError,
    paymentMethods,
    commitmentsByPaymentMethodId,
    commitments,
  } = usePaymentMethodsWithCommitments();

  return {
    isLoading,
    hasError,
    paymentMethods,
    commitmentsByPaymentMethodId,
    commitments,
  };
};

export const PaymentMethodList = () => {
  const { t } = useTranslation('my');
  const {
    isLoading,
    hasError,
    paymentMethods,
    commitmentsByPaymentMethodId,
    commitments,
  } = useComponentData();
  const [updatedCommitments, setUpdatedCommitments] = useState<
    GraphQLCommitment[]
  >([]);

  const showNoPaymentMethodsAlert =
    !hasError && paymentMethods.length === 0 && !!commitments.length;

  if (hasError && !paymentMethods?.length) {
    return (
      <Alert
        variant="negative"
        title={t('paymentMethods.queryError.title', { count: 0 })}
        message={t('paymentMethods.queryError.message')}
        action={
          <a href={t('ctas.contactSupport.url')}>
            {t('ctas.contactSupport.text')}
          </a>
        }
      />
    );
  }

  return (
    <>
      <PrimerError keysToAlert={['commitments', 'paymentMethods']} />

      {!isLoading && <PaymentIssueAlert isSimple onlyShowMissingPayment />}

      <LocalAlerts className="flex flex-col gap-comfy-sm" />
      <MakeOneTimePaymentAlert
        updatedCommitments={updatedCommitments}
        currentCommitments={commitments}
        presentationContext="paymentSettings"
      />
      {!isLoading && showNoPaymentMethodsAlert && (
        <Alert
          variant="warning"
          title={t('payments.alerts.noPaymentMethods.title')}
          message={
            <IntlBlock i18nKey="my:payments.alerts.noPaymentMethods.message" />
          }
          dismissible={false}
        />
      )}

      <SectionContent
        heading={t('payments.methods.heading')}
        isLoadingContent={isLoading}
      >
        {!isLoading &&
          !!paymentMethods?.length &&
          paymentMethods
            .filter((method) => !!method.id)
            .map((method) => {
              return (
                <PaymentMethodCard
                  key={`payment-method-card-${method.id}`}
                  paymentMethod={method}
                  paymentMethodCommitments={
                    commitmentsByPaymentMethodId?.[method.id!] ?? []
                  }
                  allCommitments={commitments}
                  onCommitmentsUpdated={setUpdatedCommitments}
                />
              );
            })}
        <PaymentMethodAddSheet
          onCommitmentsUpdated={setUpdatedCommitments}
          commitments={commitments}
          openTrigger={
            <AddPaymentMethodButton>
              <AddPaymentMethodButtonLabel>
                {t('payments.addPaymentMethod')}
              </AddPaymentMethodButtonLabel>
            </AddPaymentMethodButton>
          }
        />
      </SectionContent>
    </>
  );
};

export const PaymentSettings = () => {
  return (
    <div className="PaymentSettings max-w-md flex flex-col gap-comfy-xl">
      <LocalAlertsProvider>
        <PaymentMethodList />
      </LocalAlertsProvider>
    </div>
  );
};
